import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { firstValueFrom } from 'rxjs';
import { Group } from '../../models';
import { GroupsService } from '../groups.service';

export type MoveChargerDialogData = {
	chargerId: string;
	currentGroupId: string;
};

export type MoveChargerDialogResult = {
	newGroupId: string;
};

@Component({
	selector: 'app-move-charger-dialog',
	imports: [MatDialogModule, MatFormFieldModule, MatSelectModule, MatButtonModule, MatProgressSpinnerModule],
	templateUrl: './move-charger-dialog.component.html',
	styleUrl: './move-charger-dialog.component.scss'
})
export class MoveChargerDialogComponent implements OnInit {
	private readonly dialogRef = inject(MatDialogRef<MoveChargerDialogComponent, MoveChargerDialogResult>);
	private readonly groupsService = inject(GroupsService);

	data = inject<MoveChargerDialogData>(MAT_DIALOG_DATA);

	groups = signal<Group[]>([]);
	loaded = signal(false);
	selectedGroupId: string | null = null;

	async ngOnInit() {
		const allGroups = await this.loadAllGroups();
		const available = allGroups.filter(g => g.groupId !== this.data.currentGroupId);
		available.sort((a, b) => a.path.localeCompare(b.path));
		this.groups.set(available);
		this.loaded.set(true);
	}

	private async loadAllGroups(): Promise<Group[]> {
		const result: Group[] = [];
		const queue: (string | null)[] = [null];

		while (queue.length > 0) {
			const parentId = queue.shift()!;
			const groups = parentId === null
				? await firstValueFrom(this.groupsService.getGroups())
				: await firstValueFrom(this.groupsService.getChildGroups(parentId));

			for (const group of groups) {
				result.push(group);
				queue.push(group.groupId);
			}
		}

		return result;
	}

	cancel() {
		this.dialogRef.close();
	}

	move() {
		if (!this.selectedGroupId) return;
		this.dialogRef.close({ newGroupId: this.selectedGroupId });
	}
}
